import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";

import ButtonPrimary from "components/buttons/ButtonPrimary.js";
import HidingMenuPublic from "./menu/HidingMenuPublic";

import heroBackground from "assets/img/landingPage/hero-bg.svg";
import circlePointBlueIcon from "assets/img/icons/icons8-circle-point-blue.svg";
import phoneMockup1 from "assets/img/landingPage/phone-mockup-1.png";

// Actions
import { openModal } from "store/actions";
import MockupDisplayEarn from "./other/landingPageEarn/MockupDisplayEarn";

const LandingPage = styled.div`
  position: relative;
  width: 100%;
  min-height: calc(100vh - 70px);

  font-family: "Montserrat", sans-serif;

  background-image: url(${heroBackground});
  background-repeat: no-repeat;
  background-color: ${css.LANDING_PAGE_BACKGROUND};
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100vh;

  background-size: 100% 100%;
  background-position: 0% 0%, 0% 0%;
  background-image: radial-gradient(
      45% 56% at 35% 21%,
      #5d56dd33 0%,
      #ff000000 99%
    ),
    radial-gradient(25% 39% at 19% 53%, #6c66d91a 0%, #ff000000 99%);
`;

const NarrowContent = styled.div`
  position: relative;
  width: 100%;

  max-width: 1300px;
  margin: 0 auto;
  padding: 0 40px;

  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;

  @media (max-width: ${globalConstants.switchMobile}) {
    padding: 0 20px;
  }
`;

const Hero = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 80px;

  @media (max-width: ${globalConstants.switchTablet}) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ShortHeading = styled.h4`
  margin-top: 16vh;
  margin-bottom: 20px;
  padding: 0 60px;
  height: 32px;

  font-family: "Barlow";

  background: ${css.BRAND_INDIGO};
  border-radius: 14px;

  font-weight: 500;
  line-height: 1;

  display: flex;
  align-items: center;

  @media (max-width: ${globalConstants.switchMobile}) {
    margin-top: 12vh;
  }
`;

const MainHeading = styled.h1`
  margin-top: 15px;

  font-size: 52px;
  font-weight: 600;
  line-height: 115%;

  @media (max-width: ${globalConstants.switchMobile}) {
    font-size: 40px;
    text-align: left;
  }
`;

const Colorful = styled.span`
  background: linear-gradient(
    270deg,
    #4891ff 13.89%,
    #8783d1 36.39%,
    #5dd39e 59.2%,
    #ffe70d 88.89%
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SubHeading = styled.h1`
  margin-top: 0px;

  font-size: 26px;
  font-weight: 600;
  line-height: 130%;

  @media (max-width: ${globalConstants.switchMobile}) {
    font-size: 20px;
  }
`;

const List = styled.div`
  margin-top: 15px;
  padding-left: 20px;

  font-size: 18px;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const ListItem = styled.div`
  display: flex;
  gap: 6px;
`;

const ListIcon = styled.div`
  width: 20px;
  min-width: 20px;
  height: 20px;
`;

const ListText = styled.div`
  line-height: 130%;
`;

const HeroParagraph = styled.div`
  margin-top: 30px;

  font-size: 20px;
  line-height: 135%;
  color: ${css.TEXT_GREY};

  @media (max-width: ${globalConstants.switchMobile}) {
    text-align: left;
    font-size: 18px;
  }
`;

const HeroButtonWrap = styled.div`
  margin-top: 40px;
  margin-bottom: 0px;

  display: flex;
  justify-content: flex-start;

  @media (max-width: ${globalConstants.switchTablet}) {
    margin-bottom: 50px;
  }
`;

const SecondContent = styled.div``;

const MockupColumn = styled.div`
  padding-top: 160px;
  min-width: 400px;

  @media (max-width: ${globalConstants.switchTablet}) {
    padding-top: 50px;
    width: 100%;
    min-width: auto;
  }
`;

const RainbowBox = styled.div`
  position: relative;
  width: 100%;
  height: 500px;

  border-radius: ${css.BOX_RADIUS_PRIMARY};
  overflow: hidden;
`;

const RainbowBg = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 300%;
  height: 100%;

  background: linear-gradient(
    132deg,
    #8783d1 0.49%,
    #ffe70d 22.08%,
    #5dd39e 43.66%,
    #8783d1 65.24%,
    #5dd39e 86.82%,
    #ffe70d 108.4%
  );
`;

const PhoneBox = styled.div`
  position: absolute;
  top: 55px;
  left: 50%;
  width: 260px;

  transform: translateX(-50%);
`;

const VideoColumn = styled.div`
  padding-top: 160px;
  min-width: 500px;

  @media (max-width: ${globalConstants.switchTablet}) {
    padding-top: 50px;
    width: 100%;
    min-width: auto;
  }
`;

export default () => {
  // set page Title
  useEffect(() => {
    document.title = "Scalelup | Drive Website Traffic, Build Awareness";
  }, []);

  const navigate = useNavigate();

  const [mockupDisplay, setMockupDisplay] = useState(1);

  const UserProperties = createSelector(
    (state) => state.User,
    (user) => ({
      profileData: user.profileData,
    })
  );

  const { profileData } = useSelector(UserProperties);

  // useEffect(() => {
  //   if (profileData) navigate("/");
  // }, [profileData]);

  const container = useRef();
  const dispatch = useDispatch();

  const tabletScreen = useMediaQuery({
    query: `(max-width: ${globalConstants.switchTablet})`,
  });

  const openSignup = () => {
    if (!profileData) dispatch(openModal("signup"));
    else navigate("/home");
  };

  const text = `Are you looking for a reliable way to earn money online?
                Scalelup offers multiple opportunities for you to boost your
                income. Without any payment required, you can earn plenty of
                rewards just through your activity. Thousands of users receive
                their payout every month. Don't miss out on the opportunity to
                earn online with Scalelup.`;

  // handle Mockup display
  const numOfDisplays = 5;

  const changeDisplay = () => {
    setMockupDisplay((prevState) => {
      if (prevState === numOfDisplays) return 1;
      else return prevState + 1;
    });
  };

  useEffect(() => {
    const displayInternal = setInterval(changeDisplay, 3000);

    return () => clearInterval(displayInternal);
  }, []);

  return (
    <LandingPage ref={container}>
      <GradientOverlay />
      <NarrowContent>
        <Hero>
          <LeftContent>
            <ShortHeading id="hero-short-heading">
              Maximize Your Profits
            </ShortHeading>
            <MainHeading>
              Earn <Colorful>Daily Rewards</Colorful>
            </MainHeading>
            <SubHeading>
              Scalelup Offers 4 Ways to <u>Earn Money Online</u> Completely
              Without Investment
            </SubHeading>
            <List>
              <ListItem>
                <ListIcon>
                  <img src={circlePointBlueIcon} style={{ width: "100%" }} />
                </ListIcon>
                <ListText>
                  Earn coins by viewing posts and exchange them for money
                </ListText>
              </ListItem>
              <ListItem>
                <ListIcon>
                  <img src={circlePointBlueIcon} style={{ width: "100%" }} />
                </ListIcon>
                <ListText>
                  Refer Scalelup and earn a cash bonus of over $400 every month
                </ListText>
              </ListItem>
              <ListItem>
                <ListIcon>
                  <img src={circlePointBlueIcon} style={{ width: "100%" }} />
                </ListIcon>
                <ListText>
                  Create a residual income with a generous affiliate program
                </ListText>
              </ListItem>
              <ListItem>
                <ListIcon>
                  <img src={circlePointBlueIcon} style={{ width: "100%" }} />
                </ListIcon>
                <ListText>
                  Be active and win up to $1,000 in our weekly contest
                </ListText>
              </ListItem>
            </List>
            {!tabletScreen && <HeroParagraph>{text}</HeroParagraph>}
          </LeftContent>
          {!tabletScreen && (
            <SecondContent>
              <MockupColumn>
                <RainbowBox>
                  <RainbowBg />
                  <PhoneBox>
                    <img src={phoneMockup1} style={{ width: "100%" }} />
                    <MockupDisplayEarn display={mockupDisplay} />
                  </PhoneBox>
                </RainbowBox>
              </MockupColumn>
            </SecondContent>
          )}
        </Hero>
        {tabletScreen && (
          <SecondContent>
            <MockupColumn>
              <RainbowBox>
                <RainbowBg />
                <PhoneBox>
                  <img src={phoneMockup1} style={{ width: "100%" }} />
                  <MockupDisplayEarn display={mockupDisplay} />
                </PhoneBox>
              </RainbowBox>
            </MockupColumn>
            <HeroParagraph>{text}</HeroParagraph>
          </SecondContent>
        )}
        <HeroButtonWrap>
          <ButtonPrimary onClick={openSignup}>
            Create Free Account
          </ButtonPrimary>
        </HeroButtonWrap>
      </NarrowContent>
      <HidingMenuPublic />
    </LandingPage>
  );
};
